import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchEvent, fetchEvents } from '../api';

const EventsContext = createContext();

/**
 * EventsProvider - Comprehensive event management context
 * 
 * Manages:
 * - Full list of all events (cached)
 * - Active events list (filtered)
 * - Currently selected event across the application
 * - Loading states
 * - Automatic refresh capabilities
 * 
 * Usage:
 * - Wrap your app with <EventsProvider>
 * - Use useEvents() hook to access events and selection state
 */
export const EventsProvider = ({ children }) => {
  // Core state
  const [allEvents, setAllEvents] = useState([]);
  const [activeEvents, setActiveEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  
  // Cache timestamp for optional refresh logic
  const [lastFetched, setLastFetched] = useState(null);

  /**
   * Load all events from the API
   */
  const loadAllEvents = useCallback(async (options = {}) => {
    const { showLoading = true, order = 'desc' } = options;
    
    try {
      if (showLoading) {
        setIsLoading(true);
      } else {
        setIsRefreshing(true);
      }
      setError(null);

      const eventsData = await fetchEvents({ active: 'false', order });
      setAllEvents(eventsData);
      
      // Filter active events (status === 'Active')
      const activeEventsData = eventsData.filter(event => event.status === 'Active');
      setActiveEvents(activeEventsData);
      
      setLastFetched(new Date());
    } catch (err) {
      console.error('Error loading events:', err);
      setError(err.message || 'Failed to load events');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  /**
   * Load active events only (optimized for pages that only need active events)
   */
  const loadActiveEvents = useCallback(async (options = {}) => {
    const { showLoading = true, order = 'desc' } = options;
    
    try {
      if (showLoading) {
        setIsLoading(true);
      } else {
        setIsRefreshing(true);
      }
      setError(null);

      const eventsData = await fetchEvents({ active: 'true', order });
      setActiveEvents(eventsData);
      
      // If we don't have all events yet, also set them
      if (allEvents.length === 0) {
        setAllEvents(eventsData);
      }
      
      setLastFetched(new Date());
    } catch (err) {
      console.error('Error loading active events:', err);
      setError(err.message || 'Failed to load active events');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [allEvents.length]);

  /**
   * Select an event by ID
   */
  const selectEvent = useCallback(async (eventId) => {
    if (!eventId) {
      setSelectedEventId(null);
      setSelectedEvent(null);
      return;
    }

    setSelectedEventId(eventId);
    
    // Try to find event in cached data first
    const cachedEvent = allEvents.find(e => e.event_id === eventId) || 
                        activeEvents.find(e => e.event_id === eventId);
    
    if (cachedEvent) {
      setSelectedEvent(cachedEvent);
    } else {
      // Fetch event details if not in cache
      try {
        const eventData = await fetchEvent(eventId);
        setSelectedEvent(eventData);
        
        // Add to cache if not already present
        setAllEvents(prev => {
          // Check if event already exists to avoid duplicates
          if (prev.find(e => e.event_id === eventId)) {
            return prev;
          }
          return [eventData, ...prev];
        });
        
        // Add to activeEvents if the event is active
        if (eventData.status === 'Active') {
          setActiveEvents(prev => {
            // Check if event already exists to avoid duplicates
            if (prev.find(e => e.event_id === eventId)) {
              return prev;
            }
            return [eventData, ...prev];
          });
        }
      } catch (err) {
        console.error(`Error fetching event ${eventId}:`, err);
        setError(err.message || 'Failed to fetch event details');
      }
    }
  }, [allEvents, activeEvents]);

  /**
   * Clear selected event
   */
  const clearSelection = useCallback(() => {
    setSelectedEventId(null);
    setSelectedEvent(null);
  }, []);

  /**
   * Refresh events (background refresh without loading state)
   */
  const refreshEvents = useCallback(async (activeOnly = false) => {
    if (activeOnly) {
      await loadActiveEvents({ showLoading: false });
    } else {
      await loadAllEvents({ showLoading: false });
    }
  }, [loadAllEvents, loadActiveEvents]);

  /**
   * Get event by ID from cache
   */
  const getEventById = useCallback((eventId) => {
    return allEvents.find(e => e.event_id === eventId) || 
           activeEvents.find(e => e.event_id === eventId);
  }, [allEvents, activeEvents]);

  /**
   * Check if an event is active
   */
  const isEventActive = useCallback((eventId) => {
    return activeEvents.some(e => e.event_id === eventId);
  }, [activeEvents]);

  // Initialize: Load all events on mount
  useEffect(() => {
    loadAllEvents({ order: 'desc' });
  }, [loadAllEvents]);

  const value = {
    // Data
    allEvents,
    activeEvents,
    selectedEventId,
    selectedEvent,
    
    // Loading states
    isLoading,
    isRefreshing,
    error,
    lastFetched,
    
    // Actions
    loadAllEvents,
    loadActiveEvents,
    selectEvent,
    clearSelection,
    refreshEvents,
    
    // Utilities
    getEventById,
    isEventActive,
  };

  return (
    <EventsContext.Provider value={value}>
      {children}
    </EventsContext.Provider>
  );
};

/**
 * Hook to use the Events context
 * 
 * @returns {Object} Events context value
 * @example
 * const { 
 *   allEvents, 
 *   activeEvents, 
 *   selectedEvent, 
 *   selectEvent,
 *   isLoading 
 * } = useEvents();
 */
export const useEvents = () => {
  const context = useContext(EventsContext);
  if (context === undefined) {
    throw new Error('useEvents must be used within an EventsProvider');
  }
  return context;
};